import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { apiClient } from '@/services/api-client';
import type { ResetResult, User } from '@/types';
import { ResetAgentDataPreview } from './ResetAgentDataPreview';

interface ResetAgentDataDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (result: ResetResult) => void;
  onForbidden?: () => void;
}

type Step = 'preview' | 'confirm';

function isPreconditionError(code?: string) {
  return code === 'PRECONDITION_FAILED' || code === 'PRECONDITION_FAILED_ERROR';
}

function isForbiddenError(code?: string) {
  return code === 'FORBIDDEN';
}

function normalizeEmail(value: string) {
  return value.trim().toLowerCase();
}

export function ResetAgentDataDialog({
  user,
  open,
  onOpenChange,
  onSuccess,
  onForbidden,
}: ResetAgentDataDialogProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('preview');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStep('preview');
      setConfirmEmail('');
      setConfirmError(null);
      setSubmitError(null);
    }
  }, [open, user?.id]);

  const previewQuery = useQuery<ResetResult, Error>({
    queryKey: ['admin', 'users', user?.id, 'reset-agent-data', 'preview'],
    enabled: open && !!user,
    staleTime: 0,
    retry: false,
    queryFn: async () => {
      const response = await apiClient.post<ResetResult>(
        `/api/v1/admin/users/${user!.id}/reset-agent-data`,
        { confirmEmail: user!.email, dryRun: true }
      );
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to preview reset');
      }
      return response.data;
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ResetResult>(
        `/api/v1/admin/users/${user!.id}/reset-agent-data`,
        { confirmEmail, dryRun: false }
      );
      if (!response.success || !response.data) {
        const error = new Error(
          response.error?.message ?? 'Failed to reset agent data'
        ) as Error & {
          code?: string;
        };
        error.code = response.error?.code;
        throw error;
      }
      return response.data;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'detail', user!.id] });
      onOpenChange(false);
      onSuccess?.(result);
    },
    onError: (error: Error & { code?: string }) => {
      if (isPreconditionError(error.code)) {
        setConfirmError('Email did not match');
        return;
      }
      if (isForbiddenError(error.code)) {
        onOpenChange(false);
        onForbidden?.();
        return;
      }
      setSubmitError('Something went wrong while resetting agent data. Please try again.');
    },
  });

  const isConfirmValid = useMemo(
    () => !!user && normalizeEmail(confirmEmail) === normalizeEmail(user.email),
    [confirmEmail, user]
  );

  if (!user) return null;

  const preview = previewQuery.data;
  const isResetting = resetMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Reset Agent Data</DialogTitle>
          <DialogDescription>
            Preview and reset interviewer-captured state for {user.email}. Account, persona, story
            card mapping, and onboarding are kept.
          </DialogDescription>
        </DialogHeader>

        {step === 'preview' ? (
          <div className="space-y-4 py-4">
            {previewQuery.isLoading && (
              <div className="flex min-h-[12rem] items-center justify-center text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading reset preview...
              </div>
            )}

            {previewQuery.error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {previewQuery.error.message}
              </div>
            )}

            {preview && <ResetAgentDataPreview preview={preview} />}
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              <div className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  This permanently wipes the previewed agent data. Type the user's email to confirm:{' '}
                  <span className="font-semibold">{user.email}</span>
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-email">User email</Label>
              <Input
                id="confirm-email"
                value={confirmEmail}
                onChange={(event) => {
                  setConfirmEmail(event.target.value);
                  setConfirmError(null);
                  setSubmitError(null);
                }}
                className={cn(confirmError && 'border-destructive focus-visible:ring-destructive')}
                autoComplete="off"
              />
              {confirmError && <p className="text-sm text-destructive">{confirmError}</p>}
            </div>

            {submitError && <p className="text-sm text-destructive">{submitError}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isResetting}>
            Cancel
          </Button>
          {step === 'preview' ? (
            <Button
              onClick={() => setStep('confirm')}
              disabled={!preview || previewQuery.isLoading || !!previewQuery.error}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={() => resetMutation.mutate()}
              disabled={!isConfirmValid || isResetting}
            >
              {isResetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reset
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
